import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Plus,
  RefreshCw,
  Wand2,
  Pencil,
  Trash2,
  Check,
  X,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  useLLDPractice,
  UMLElement,
  UMLDiagramData,
} from '../../context/LLDPracticeContext';
import { autoGenerateUML } from '../../utils/umlAutoGenerator';

// ─── Types ──────────────────────────────────────────────────────────────────────

type ElementType = 'class' | 'interface' | 'abstract-class';

interface FieldInput {
  access: '+' | '-' | '#';
  name: string;
  type: string;
}

interface MethodInput {
  access: '+' | '-' | '#';
  name: string;
  parameters: string;
  returnType: string;
}

// ─── Helper Functions ───────────────────────────────────────────────────────────

function getAccessColor(access: '+' | '-' | '#'): string {
  switch (access) {
    case '+':
      return 'text-primary-600 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30';
    case '-':
      return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800';
    case '#':
      return 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800';
  }
}

function getTypeBadgeStyle(type: ElementType): string {
  switch (type) {
    case 'class':
      return 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300';
    case 'interface':
      return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
    case 'abstract-class':
      return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
  }
}

function getTypeLabel(type: ElementType): string {
  switch (type) {
    case 'class':
      return 'C';
    case 'interface':
      return 'I';
    case 'abstract-class':
      return 'A';
  }
}

function getRelationshipSymbol(type: string): string {
  switch (type) {
    case 'association':
      return '→';
    case 'aggregation':
      return '◇→';
    case 'composition':
      return '◆→';
    case 'inheritance':
      return '▷→';
    case 'dependency':
      return '- - →';
    default:
      return '→';
  }
}

// ─── Component ──────────────────────────────────────────────────────────────────

const UMLBuilder: React.FC = () => {
  const { state, dispatch } = useLLDPractice();
  const { entities, responsibilities, relationships, umlDiagram } = state.workspaceState;

  // Track whether we've auto-generated and if source data changed since
  const [autoGenerated, setAutoGenerated] = useState(false);
  const [showResyncNotification, setShowResyncNotification] = useState(false);
  const prevDataHashRef = useRef<string>('');

  // Editing state
  const [editingElementId, setEditingElementId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<ElementType>('class');
  const [showAddElement, setShowAddElement] = useState(false);
  const [newElementName, setNewElementName] = useState('');
  const [newElementType, setNewElementType] = useState<ElementType>('class');

  // Add field/method forms
  const [addingFieldTo, setAddingFieldTo] = useState<string | null>(null);
  const [newField, setNewField] = useState<FieldInput>({ access: '+', name: '', type: '' });
  const [addingMethodTo, setAddingMethodTo] = useState<string | null>(null);
  const [newMethod, setNewMethod] = useState<MethodInput>({ access: '+', name: '', parameters: '', returnType: '' });

  // Collapsed state for class cards
  const [collapsedElements, setCollapsedElements] = useState<Set<string>>(new Set());

  // ─── Derived State ──────────────────────────────────────────────────────────

  const elements = umlDiagram.elements;
  const connections = umlDiagram.connections;

  const canAutoGenerate = entities.length > 0;

  // ─── Detect upstream data changes after auto-generation ─────────────────────

  useEffect(() => {
    if (!autoGenerated) return;

    const currentHash = JSON.stringify({
      entities: entities.map((e) => e.id),
      responsibilities: Object.keys(responsibilities),
      relationships: relationships.map((r) => r.id),
    });

    if (prevDataHashRef.current && prevDataHashRef.current !== currentHash) {
      setShowResyncNotification(true);
    }

    prevDataHashRef.current = currentHash;
  }, [entities, responsibilities, relationships, autoGenerated]);

  // ─── Dispatch helper ────────────────────────────────────────────────────────

  const updateDiagram = useCallback(
    (data: UMLDiagramData) => {
      dispatch({ type: 'UPDATE_UML', payload: data });
    },
    [dispatch]
  );

  // ─── Auto-generate ──────────────────────────────────────────────────────────

  const handleAutoGenerate = useCallback(() => {
    // Build responsibilities in the format expected by autoGenerateUML
    const responsibilitiesMap: Record<string, { entityId: string; attributes: { id: string; name: string; type: string; accessModifier: 'public' | 'private' | 'protected' }[]; methods: { id: string; name: string; parameters: { name: string; type: string }[]; returnType: string; accessModifier: 'public' | 'private' | 'protected' }[] }> = {};

    for (const [entityId, resp] of Object.entries(responsibilities)) {
      responsibilitiesMap[entityId] = {
        entityId: resp.entityId,
        attributes: resp.attributes.map((a) => ({
          id: a.id,
          name: a.name,
          type: a.type,
          accessModifier: a.accessModifier,
        })),
        methods: resp.methods.map((m) => ({
          id: m.id,
          name: m.name,
          parameters: m.parameters,
          returnType: m.returnType,
          accessModifier: m.accessModifier,
        })),
      };
    }

    // Map relationships to expected format
    const mappedRelationships = relationships.map((r) => ({
      id: r.id,
      fromEntityId: r.sourceEntityId,
      toEntityId: r.targetEntityId,
      type: r.type,
      cardinality: r.cardinality,
    }));

    const result = autoGenerateUML(entities, responsibilitiesMap, mappedRelationships);
    updateDiagram(result);
    setAutoGenerated(true);
    setShowResyncNotification(false);
    prevDataHashRef.current = JSON.stringify({
      entities: entities.map((e) => e.id),
      responsibilities: Object.keys(responsibilities),
      relationships: relationships.map((r) => r.id),
    });
  }, [entities, responsibilities, relationships, updateDiagram]);

  // ─── Re-sync ────────────────────────────────────────────────────────────────

  const handleResync = useCallback(() => {
    handleAutoGenerate();
  }, [handleAutoGenerate]);

  const dismissResync = useCallback(() => {
    setShowResyncNotification(false);
  }, []);

  // ─── Add Class / Interface / Abstract Class ─────────────────────────────────

  const handleAddElement = useCallback(
    () => {
      if (elements.length >= 50 || !newElementName.trim()) return;

      const col = elements.length % 3;
      const row = Math.floor(elements.length / 3);

      const newElement: UMLElement = {
        id: `uml-manual-${crypto.randomUUID()}`,
        type: newElementType,
        name: newElementName.trim(),
        x: 50 + col * 300,
        y: 50 + row * 250,
        fields: [],
        methods: [],
      };

      updateDiagram({
        elements: [...elements, newElement],
        connections,
      });
      setNewElementName('');
      setNewElementType('class');
      setShowAddElement(false);
    },
    [elements, connections, newElementName, newElementType, updateDiagram]
  );

  // ─── Remove Element ─────────────────────────────────────────────────────────

  const handleRemoveElement = useCallback(
    (id: string) => {
      updateDiagram({
        elements: elements.filter((el) => el.id !== id),
        connections: connections.filter((c) => c.fromId !== id && c.toId !== id),
      });
    },
    [elements, connections, updateDiagram]
  );

  // ─── Edit Element Name/Type ─────────────────────────────────────────────────

  const handleStartEdit = useCallback((element: UMLElement) => {
    setEditingElementId(element.id);
    setEditName(element.name);
    setEditType(element.type);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editingElementId || !editName.trim()) return;

    updateDiagram({
      elements: elements.map((el) =>
        el.id === editingElementId
          ? { ...el, name: editName.trim(), type: editType }
          : el
      ),
      connections,
    });

    setEditingElementId(null);
    setEditName('');
  }, [editingElementId, editName, editType, elements, connections, updateDiagram]);

  const handleCancelEdit = useCallback(() => {
    setEditingElementId(null);
    setEditName('');
  }, []);

  // ─── Add Field ──────────────────────────────────────────────────────────────

  const handleAddField = useCallback(
    (elementId: string) => {
      if (!newField.name.trim() || !newField.type.trim()) return;

      updateDiagram({
        elements: elements.map((el) =>
          el.id === elementId
            ? {
              ...el,
              fields: [
                ...el.fields,
                { access: newField.access, name: newField.name.trim(), type: newField.type.trim() },
              ],
            }
            : el
        ),
        connections,
      });

      setNewField({ access: '+', name: '', type: '' });
      setAddingFieldTo(null);
    },
    [newField, elements, connections, updateDiagram]
  );

  // ─── Remove Field ───────────────────────────────────────────────────────────

  const handleRemoveField = useCallback(
    (elementId: string, fieldIndex: number) => {
      updateDiagram({
        elements: elements.map((el) =>
          el.id === elementId
            ? { ...el, fields: el.fields.filter((_, i) => i !== fieldIndex) }
            : el
        ),
        connections,
      });
    },
    [elements, connections, updateDiagram]
  );

  // ─── Add Method ─────────────────────────────────────────────────────────────

  const handleAddMethod = useCallback(
    (elementId: string) => {
      if (!newMethod.name.trim() || !newMethod.returnType.trim()) return;

      const signature = `${newMethod.name.trim()}(${newMethod.parameters.trim()}): ${newMethod.returnType.trim()}`;

      updateDiagram({
        elements: elements.map((el) =>
          el.id === elementId
            ? {
              ...el,
              methods: [
                ...el.methods,
                { access: newMethod.access, signature },
              ],
            }
            : el
        ),
        connections,
      });

      setNewMethod({ access: '+', name: '', parameters: '', returnType: '' });
      setAddingMethodTo(null);
    },
    [newMethod, elements, connections, updateDiagram]
  );

  // ─── Remove Method ──────────────────────────────────────────────────────────

  const handleRemoveMethod = useCallback(
    (elementId: string, methodIndex: number) => {
      updateDiagram({
        elements: elements.map((el) =>
          el.id === elementId
            ? { ...el, methods: el.methods.filter((_, i) => i !== methodIndex) }
            : el
        ),
        connections,
      });
    },
    [elements, connections, updateDiagram]
  );

  // ─── Toggle collapse ────────────────────────────────────────────────────────

  const toggleCollapse = useCallback((id: string) => {
    setCollapsedElements((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="uml-builder min-h-0">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-800 flex-wrap">
        <button
          onClick={handleAutoGenerate}
          disabled={!canAutoGenerate}
          className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
          title={
            canAutoGenerate
              ? 'Build UML from entities, responsibilities and relationships from earlier steps'
              : 'Discover at least one entity first'
          }
        >
          <Wand2 className="w-4 h-4" />
          Auto-generate
        </button>

        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

        <button
          onClick={() => setShowAddElement((value) => !value)}
          disabled={elements.length >= 50}
          className="px-2.5 py-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add element
        </button>

        <div className="ml-auto hidden sm:flex items-center gap-3 text-[11px] text-gray-500">
          <span><b className="text-primary-400">+</b> public</span><span><b>−</b> private</span><span><b>#</b> protected</span>
        </div>
      </div>

      {showAddElement && (
        <div className="uml-add-element mx-4 mt-3 flex flex-wrap items-end gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <label className="grid gap-1 flex-1 min-w-[180px]"><span className="text-[11px] font-medium text-gray-500">Element name</span><input autoFocus value={newElementName} onChange={(e) => setNewElementName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAddElement(); if (e.key === 'Escape') setShowAddElement(false); }} placeholder="e.g. PaymentService" className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm" /></label>
          <label className="grid gap-1"><span className="text-[11px] font-medium text-gray-500">Kind</span><select value={newElementType} onChange={(e) => setNewElementType(e.target.value as ElementType)} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"><option value="class">Class</option><option value="interface">Interface</option><option value="abstract-class">Abstract class</option></select></label>
          <button type="button" onClick={handleAddElement} disabled={!newElementName.trim()} className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-40">Create</button>
          <button type="button" onClick={() => setShowAddElement(false)} className="px-3 py-2 text-sm text-gray-500">Cancel</button>
        </div>
      )}

      {/* Re-sync Notification */}
      {showResyncNotification && (
        <div className="mx-4 mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Entity or relationship data has changed since auto-generation.
            </p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleResync}
                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium flex items-center gap-1 transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Re-sync
              </button>
              <button
                onClick={dismissResync}
                className="px-3 py-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs font-medium transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="uml-workflow-note mx-4 mt-4">
        <Wand2 aria-hidden="true" />
        <div>
          <strong>Build from your earlier decisions</strong>
          <span>Auto-generate brings together the entities, responsibilities and relationships from Steps 2–5. Check the overall shape, then refine the class details.</span>
        </div>
      </div>

      {elements.length > 0 && <UMLDiagramPreview elements={elements} connections={connections} />}

      {/* Relationship summary stays in the document flow so it never overlays the diagram. */}
      {connections.length > 0 && (
        <section className="uml-relationships mx-4 mt-4" aria-labelledby="uml-relationships-title">
          <div className="uml-relationships__heading">
            <div>
              <span>Structure</span>
              <h4 id="uml-relationships-title">Relationships</h4>
            </div>
            <strong>{connections.length}</strong>
          </div>
          <div className="uml-relationships__list">
            {connections.map((conn) => {
              const fromElement = elements.find((el) => el.id === conn.fromId);
              const toElement = elements.find((el) => el.id === conn.toId);
              return (
                <div key={conn.id} className="uml-relationship-row">
                  <div className="uml-relationship-row__path">
                    <span className="uml-relationship-row__entity">{fromElement?.name || '?'}</span>
                    <span className="uml-relationship-row__connector" title={conn.type}>
                      <small>{conn.type}</small>
                      <b>{getRelationshipSymbol(conn.type)}</b>
                    </span>
                    <span className="uml-relationship-row__entity">{toElement?.name || '?'}</span>
                  </div>
                  <span className="uml-relationship-row__cardinality">{conn.cardinality.replace('-to-', ' → ')}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Canvas Area - Grid of class cards */}
      <div className="px-4 py-4">
        {elements.length === 0 ? (
          <div className="h-full min-h-[200px] flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-center px-8 py-8">
            <Wand2 className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">No UML elements yet.</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5 max-w-sm">
              {canAutoGenerate
                ? 'Click "Auto-generate" to build a diagram from your entities, or add a class manually.'
                : 'Discover entities in Step 2 first, or create a named class, interface or abstract class manually.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {elements.map((element) => (
              <UMLClassCard
                key={element.id}
                element={element}
                isEditing={editingElementId === element.id}
                editName={editName}
                editType={editType}
                isCollapsed={collapsedElements.has(element.id)}
                addingField={addingFieldTo === element.id}
                addingMethod={addingMethodTo === element.id}
                newField={newField}
                newMethod={newMethod}
                onToggleCollapse={() => toggleCollapse(element.id)}
                onStartEdit={() => handleStartEdit(element)}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onEditNameChange={setEditName}
                onEditTypeChange={setEditType}
                onRemove={() => handleRemoveElement(element.id)}
                onStartAddField={() => {
                  setAddingFieldTo(element.id);
                  setNewField({ access: '+', name: '', type: '' });
                }}
                onCancelAddField={() => setAddingFieldTo(null)}
                onFieldChange={setNewField}
                onAddField={() => handleAddField(element.id)}
                onRemoveField={(idx) => handleRemoveField(element.id, idx)}
                onStartAddMethod={() => {
                  setAddingMethodTo(element.id);
                  setNewMethod({ access: '+', name: '', parameters: '', returnType: '' });
                }}
                onCancelAddMethod={() => setAddingMethodTo(null)}
                onMethodChange={setNewMethod}
                onAddMethod={() => handleAddMethod(element.id)}
                onRemoveMethod={(idx) => handleRemoveMethod(element.id, idx)}
              />
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

const UMLDiagramPreview: React.FC<{ elements: UMLElement[]; connections: UMLDiagramData['connections'] }> = ({ elements, connections }) => {
  const width = 860;
  const columns = Math.min(2, Math.max(1, elements.length));
  const rows = Math.ceil(elements.length / columns);
  const height = Math.max(190, rows * 165 + 30);
  const positions = new Map(elements.map((element, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);
    const nodeWidth = columns === 1 ? 320 : 290;
    const columnSpace = width / columns;
    return [element.id, { x: col * columnSpace + (columnSpace - nodeWidth) / 2, y: 32 + row * 165, width: nodeWidth }];
  }));

  return (
    <div className="uml-preview mx-4 mt-4">
      <div className="uml-preview__header"><div><span>Diagram preview</span><strong>{elements.length} element{elements.length === 1 ? '' : 's'} <i /> {connections.length} relationship{connections.length === 1 ? '' : 's'}</strong></div><small>Generated from your design decisions</small></div>
      <div className="overflow-x-auto"><svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="UML class relationship diagram">
        <defs><marker id="uml-preview-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" /></marker></defs>
        {connections.map((connection) => {
          const from = positions.get(connection.fromId); const to = positions.get(connection.toId);
          if (!from || !to) return null;
          const sameRow = Math.abs(from.y - to.y) < 10;
          const fromIsLeft = from.x < to.x;
          const startX = sameRow ? (fromIsLeft ? from.x + from.width : from.x) : from.x + from.width / 2;
          const startY = sameRow ? from.y + 46 : from.y + 92;
          const endX = sameRow ? (fromIsLeft ? to.x : to.x + to.width) : to.x + to.width / 2;
          const endY = sameRow ? to.y + 46 : to.y;
          return <g key={connection.id} className="uml-preview__connection"><path d={`M ${startX} ${startY} L ${endX} ${endY}`} /></g>;
        })}
        {elements.map((element) => {
          const position = positions.get(element.id)!;
          const fieldLabel = `${element.fields.length} field${element.fields.length === 1 ? '' : 's'}`;
          const methodLabel = `${element.methods.length} method${element.methods.length === 1 ? '' : 's'}`;
          return <g key={element.id} className="uml-preview__node"><rect x={position.x} y={position.y} width={position.width} height="92" rx="8"/><text className="uml-preview__stereotype" x={position.x + position.width / 2} y={position.y + 19}>{element.type === 'interface' ? '«interface»' : element.type === 'abstract-class' ? '«abstract»' : '«class»'}</text><text x={position.x + position.width / 2} y={position.y + 43}>{element.name}</text><line x1={position.x} x2={position.x + position.width} y1={position.y + 58} y2={position.y + 58} /><text className="uml-preview__meta" x={position.x + position.width / 2} y={position.y + 78}>{fieldLabel} <tspan>·</tspan> {methodLabel}</text></g>;
        })}
      </svg></div>
    </div>
  );
};

// ─── UML Class Card Sub-Component ───────────────────────────────────────────────

interface UMLClassCardProps {
  element: UMLElement;
  isEditing: boolean;
  editName: string;
  editType: ElementType;
  isCollapsed: boolean;
  addingField: boolean;
  addingMethod: boolean;
  newField: FieldInput;
  newMethod: MethodInput;
  onToggleCollapse: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditNameChange: (name: string) => void;
  onEditTypeChange: (type: ElementType) => void;
  onRemove: () => void;
  onStartAddField: () => void;
  onCancelAddField: () => void;
  onFieldChange: (field: FieldInput) => void;
  onAddField: () => void;
  onRemoveField: (index: number) => void;
  onStartAddMethod: () => void;
  onCancelAddMethod: () => void;
  onMethodChange: (method: MethodInput) => void;
  onAddMethod: () => void;
  onRemoveMethod: (index: number) => void;
}

const UMLClassCard: React.FC<UMLClassCardProps> = ({
  element,
  isEditing,
  editName,
  editType,
  isCollapsed,
  addingField,
  addingMethod,
  newField,
  newMethod,
  onToggleCollapse,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditNameChange,
  onEditTypeChange,
  onRemove,
  onStartAddField,
  onCancelAddField,
  onFieldChange,
  onAddField,
  onRemoveField,
  onStartAddMethod,
  onCancelAddMethod,
  onMethodChange,
  onAddMethod,
  onRemoveMethod,
}) => {
  return (
    <div className="uml-class-card border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden bg-white dark:bg-gray-900 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all">
      {/* Card Header */}
      <div className="uml-class-card__header flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-800">
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              type="text"
              value={editName}
              onChange={(e) => onEditNameChange(e.target.value)}
              className="flex-1 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveEdit();
                if (e.key === 'Escape') onCancelEdit();
              }}
            />
            <select
              value={editType}
              onChange={(e) => onEditTypeChange(e.target.value as ElementType)}
              className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            >
              <option value="class">Class</option>
              <option value="interface">Interface</option>
              <option value="abstract-class">Abstract</option>
            </select>
            <button
              onClick={onSaveEdit}
              className="p-1 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={onCancelEdit}
              className="p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`w-5 h-5 rounded text-xs font-bold flex items-center justify-center ${getTypeBadgeStyle(element.type)}`}
                title={element.type}
              >
                {getTypeLabel(element.type)}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {element.name}
              </span>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={onToggleCollapse}
                className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={isCollapsed ? 'Expand' : 'Collapse'}
              >
                {isCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={onStartEdit}
                className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                title="Edit class"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onRemove}
                className="p-1 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors"
                title="Remove class"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Card Body - Fields and Methods */}
      {!isCollapsed && (
        <div className="uml-class-card__body divide-y divide-gray-200 dark:divide-gray-700">
          {/* Fields Section */}
          <div className="uml-member-section px-3 py-2">
            <div className="uml-member-section__heading flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                Fields
              </span>
              <button
                onClick={onStartAddField}
                className="p-0.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                title="Add field"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            {element.fields.length === 0 && !addingField && (
              <p className="text-xs text-gray-500 dark:text-gray-500 italic">No fields</p>
            )}

            <div className="space-y-0.5">
              {element.fields.map((field, idx) => (
                <div key={idx} className="uml-member-row flex items-center gap-1.5 group">
                  <span
                    className={`w-5 h-5 flex items-center justify-center rounded text-xs font-bold ${getAccessColor(field.access)}`}
                  >
                    {field.access}
                  </span>
                  <span className="text-xs text-gray-800 dark:text-gray-200 flex-1 truncate">
                    {field.name}: <span className="text-gray-600 dark:text-gray-400">{field.type}</span>
                  </span>
                  <button
                    onClick={() => onRemoveField(idx)}
                    className="p-0.5 text-gray-300 hover:text-rose-500 dark:text-gray-600 dark:hover:text-rose-400 opacity-0 group-hover:opacity-100 rounded transition-all"
                    title="Remove field"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add Field Form */}
            {addingField && (
              <div className="uml-member-editor mt-2 space-y-2">
                <div className="uml-member-form uml-member-form--field">
                  <select
                    value={newField.access}
                    onChange={(e) => onFieldChange({ ...newField, access: e.target.value as '+' | '-' | '#' })}
                    className="w-12 px-1 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                  >
                    <option value="+">+</option>
                    <option value="-">-</option>
                    <option value="#">#</option>
                  </select>
                  <input
                    type="text"
                    value={newField.name}
                    onChange={(e) => onFieldChange({ ...newField, name: e.target.value })}
                    placeholder="name"
                    className="flex-1 px-2 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onAddField();
                      if (e.key === 'Escape') onCancelAddField();
                    }}
                  />
                  <input
                    type="text"
                    value={newField.type}
                    onChange={(e) => onFieldChange({ ...newField, type: e.target.value })}
                    placeholder="type"
                    className="flex-1 px-2 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onAddField();
                      if (e.key === 'Escape') onCancelAddField();
                    }}
                  />
                </div>
                <div className="uml-member-editor__actions flex gap-2">
                  <button
                    onClick={onAddField}
                    disabled={!newField.name.trim() || !newField.type.trim()}
                    className="px-2.5 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                  <button
                    onClick={onCancelAddField}
                    className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Methods Section */}
          <div className="uml-member-section px-3 py-2">
            <div className="uml-member-section__heading flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                Methods
              </span>
              <button
                onClick={onStartAddMethod}
                className="p-0.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                title="Add method"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            {element.methods.length === 0 && !addingMethod && (
              <p className="text-xs text-gray-500 dark:text-gray-500 italic">No methods</p>
            )}

            <div className="space-y-0.5">
              {element.methods.map((method, idx) => (
                <div key={idx} className="uml-member-row flex items-center gap-1.5 group">
                  <span
                    className={`w-5 h-5 flex items-center justify-center rounded text-xs font-bold ${getAccessColor(method.access)}`}
                  >
                    {method.access}
                  </span>
                  <span className="text-xs text-gray-800 dark:text-gray-200 flex-1 truncate">
                    {method.signature}
                  </span>
                  <button
                    onClick={() => onRemoveMethod(idx)}
                    className="p-0.5 text-gray-300 hover:text-rose-500 dark:text-gray-600 dark:hover:text-rose-400 opacity-0 group-hover:opacity-100 rounded transition-all"
                    title="Remove method"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add Method Form */}
            {addingMethod && (
              <div className="uml-member-editor mt-2 space-y-2">
                <div className="uml-member-form uml-member-form--method">
                  <select
                    value={newMethod.access}
                    onChange={(e) => onMethodChange({ ...newMethod, access: e.target.value as '+' | '-' | '#' })}
                    className="w-12 px-1 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                  >
                    <option value="+">+</option>
                    <option value="-">-</option>
                    <option value="#">#</option>
                  </select>
                  <input
                    type="text"
                    value={newMethod.name}
                    onChange={(e) => onMethodChange({ ...newMethod, name: e.target.value })}
                    placeholder="method name"
                    className="uml-method-name flex-1 px-2 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onAddMethod();
                      if (e.key === 'Escape') onCancelAddMethod();
                    }}
                  />
                  <input
                    type="text"
                    value={newMethod.parameters}
                    onChange={(e) => onMethodChange({ ...newMethod, parameters: e.target.value })}
                    placeholder="params (optional)"
                    aria-label="Method parameters"
                    className="uml-method-params px-2 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onAddMethod();
                      if (e.key === 'Escape') onCancelAddMethod();
                    }}
                  />
                  <input
                    type="text"
                    value={newMethod.returnType}
                    onChange={(e) => onMethodChange({ ...newMethod, returnType: e.target.value })}
                    placeholder="return type"
                    aria-label="Method return type"
                    className="uml-method-return px-2 py-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onAddMethod();
                      if (e.key === 'Escape') onCancelAddMethod();
                    }}
                  />
                </div>
                <div className="uml-member-editor__actions flex gap-2">
                  <button
                    onClick={onAddMethod}
                    disabled={!newMethod.name.trim() || !newMethod.returnType.trim()}
                    className="px-2.5 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                  <button
                    onClick={onCancelAddMethod}
                    className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UMLBuilder;
